/**
 * Monthly appointment usage — how much of the allowance the merchant has spent.
 *
 * ## Where the numbers come from
 *
 * `GET {API_BASE_URL}/v3/subscription/merchants/{merchant_id}/usage`, served by
 * subscription_service:
 *
 *     {
 *       "appointments": {
 *         "limit": 25,
 *         "created_count": 20,
 *         "period": "month",
 *         "approaching_limit": true,
 *         "booking_allowed": true
 *       }
 *     }
 *
 * A `404` means an unknown merchant id or a merchant that is not on WSB — which
 * is exactly the "uncapped, no upsell" case, so the fetcher maps it to `null`
 * rather than an error. See `utils/fetch-appointment-usage.ts`.
 *
 * The only route into `appointmentUsage`. Core's realtime appointment-created
 * event carries no counts, so it prompts a re-read of this endpoint rather than
 * supplying figures — see `subscriptions/appointment-created-push.ts`.
 *
 * `period` is `"month"` and is not carried into state: no copy quotes the period,
 * so nothing downstream would use it. Should the backend start returning
 * something else, the modal titles ("You've created 20 of 25 appointments") stay
 * accurate — they never name a timespan.
 *
 * ## The two verdicts
 *
 * `approaching_limit` and `booking_allowed` are the backend's own reading of the
 * counts, and `booking_allowed` is the same boolean the booking widget receives
 * from `/booking_data`. They decide which state a merchant is in; see
 * `classifyUsage`.
 */
export interface AppointmentUsage {
  /** Appointments created in the current merchant-local month. */
  used: number;
  /** The monthly allowance. */
  limit: number;
  /**
   * The backend's verdicts, as of the moment usage was fetched. Optional
   * defensively — subscription_service always sends both — and an absent
   * `approachingLimit` simply means the warning state is never entered.
   */
  approachingLimit?: boolean;
  bookingAllowed?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Narrows a `/usage` response body, or `null` when it carries no usable
 * appointment counts.
 *
 * Deliberately strict about the two numbers and lenient about everything else:
 * without both counts there is no "20 of 25" to render, but a missing verdict
 * or an unknown extra key is survivable.
 */
export function parseAppointmentUsage(body: unknown): AppointmentUsage | null {
  if (!isRecord(body)) return null;

  const appointments = body.appointments;
  if (!isRecord(appointments)) return null;

  const { limit, created_count: createdCount } = appointments;

  if (typeof limit !== 'number' || typeof createdCount !== 'number') {
    return null;
  }

  return {
    used: createdCount,
    limit,
    ...(typeof appointments.approaching_limit === 'boolean' && {
      approachingLimit: appointments.approaching_limit,
    }),
    ...(typeof appointments.booking_allowed === 'boolean' && {
      bookingAllowed: appointments.booking_allowed,
    }),
  };
}

export type UsageState = 'under' | 'approaching' | 'reached';

/**
 * Classifies usage into the three states the design distinguishes.
 *
 * `approachingLimit` is the only source for the warning state: the share of the
 * allowance that counts as "approaching" is chosen by the server (80%, in
 * `SubscriptionService.AppointmentsUsage`) and is not recomputed here.
 *
 * `reached` also triggers on `used >= limit`, so a count at or past the
 * allowance blocks even if the verdict that came with it is out of date. `>=`
 * rather than `===` because Core's cap counts then creates and can overshoot
 * under concurrent bookings: a merchant at 26 of 25 is still blocked.
 */
export function classifyUsage({
  used,
  limit,
  approachingLimit,
  bookingAllowed,
}: AppointmentUsage): UsageState {
  // No allowance resolved. Core fails open in the same situation, so we must not
  // warn about a cap that is not being enforced.
  if (limit <= 0) return 'under';

  if (bookingAllowed === false || used >= limit) return 'reached';

  if (approachingLimit === true) return 'approaching';

  return 'under';
}
